import React, { useState } from 'react';
import {
  Box,
  Button,
  Center,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Spinner,
  Switch,
  Text,
  Textarea,
  useToast,
} from '@chakra-ui/react';
import { Controller } from 'react-hook-form';
import { AxiosError } from 'axios';
import DatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { es } from 'date-fns/locale';
import { PiLinkSimpleHorizontalDuotone } from 'react-icons/pi';
import api from '../../../services/api';
import { Required } from '../../../components/Required';
import { SectionHeading } from '../../../components/SectionHeading';
import { AsyncSelector } from '../../../components/AsyncSelector';
import { Branch } from '../../../domain/branch';
import { NewPaymentLinkForm, useCreatePaymentLinkForm } from '../hooks/useCreatePaymentLinkForm';
import { CreatePaymentLinkResponse, CreatePaymentLinkResponseData, toCreatePaymentLinkRequest } from '../models/create-payment-link';
import { PAYMENT_LINKS_ENDPOINTS } from '../links.constants';

registerLocale('es', es);

// Comercio de pruebas fijo (id_merchant 40 = "Pruebas Wuzi", confirmado contra el BFF
// real) para poder generar links de prueba sin depender de que el endpoint de
// sucursales lo incluya. El selector solo lee id/fantasy_name, así que el resto de
// los campos del dominio Branch no se usan aquí — de ahí el cast.
// Comentado para el PR a development: no se debe forzar un comercio de pruebas por
// default, el usuario debe seleccionar su propia sucursal real.
// const TEST_BRANCH = { id: '40', fantasy_name: 'Pruebas Wuzi' } as Branch;

interface GeneratePaymentLinkFormProps {
  onLinkCreated: (data: CreatePaymentLinkResponseData, branchName: string) => void;
}

export const GeneratePaymentLinkForm = ({ onLinkCreated }: GeneratePaymentLinkFormProps) => {
  const { controls, hasExpirationDate, handleSubmit, reset, control, errors } = useCreatePaymentLinkForm();
  const toast = useToast();

  const [creatingLink, setCreatingLink] = useState(false);
  const [selectedBranchName, setSelectedBranchName] = useState('');

  const submit = async (formData: NewPaymentLinkForm) => {
    setCreatingLink(true);
    try {
      const response = await api.post<CreatePaymentLinkResponse>(PAYMENT_LINKS_ENDPOINTS.create, toCreatePaymentLinkRequest(formData));
      toast({
        status: 'success',
        title: 'Operación exitosa',
        position: 'bottom-right',
        description: 'El link de pago ha sido generado correctamente.',
      });
      onLinkCreated(response.data.data, selectedBranchName);
      reset();
      setSelectedBranchName('');
    } catch (e) {
      const reason = e instanceof AxiosError ? e.response?.data?.message : undefined;
      toast({
        position: 'bottom-right',
        status: 'error',
        title: 'Error',
        description: reason
          ? `El link de pago no ha sido generado: ${reason}`
          : 'El link de pago no ha sido generado, espere un momento e intente nuevamente.',
      });
    } finally {
      setCreatingLink(false);
    }
  };

  return (
    <Box backgroundColor="white" borderRadius="16px" padding="xxl">
      <SectionHeading label="Crear Link de Pago" />
      <Text fontSize="s" color="grey.600" marginBottom="xxl">
        Genera un link de pago para compartir y recibe pagos en cualquier momento y en cualquier lugar.
      </Text>

      <Center marginBottom="xxl">
        <PiLinkSimpleHorizontalDuotone fontSize="5rem" color="var(--chakra-colors-info-800)" />
      </Center>

      <form onSubmit={handleSubmit(submit)}>
        <FormControl isInvalid={!!errors.amount} marginBottom="xl">
          <FormLabel>Monto<Required /></FormLabel>
          <Input type="number" step="0.01" min="0" {...controls.amount} />
          <FormErrorMessage>{errors.amount?.message}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!errors.id_merchant} marginBottom="xl">
          <FormLabel>Selecciona una sucursal<Required /></FormLabel>
          <Controller
            name="id_merchant"
            control={control}
            rules={{ required: 'Este campo es requerido.' }}
            render={({ field }) => (
              <AsyncSelector<Branch>
                url="/transactions-view/branches"
                labelProperty="fantasy_name"
                idProperty="id"
                value={field.value}
                handleChange={(branches) => {
                  field.onChange(branches?.[0]?.id ?? '');
                  setSelectedBranchName(branches?.[0]?.fantasy_name ?? '');
                }}
                placeholder=".Selecciona una sucursal"
                width="auto"
                minWidth="15em"
       
              />
            )}
          />
          <FormErrorMessage>{errors.id_merchant?.message}</FormErrorMessage>
        </FormControl>

        <FormControl isInvalid={!!errors.concept} marginBottom="xl">
          <FormLabel>Concepto<Required /></FormLabel>
          <Textarea {...controls.concept} />
          <FormErrorMessage>{errors.concept?.message}</FormErrorMessage>
        </FormControl>

        <FormControl display="flex" alignItems="center" marginBottom="xl">
          <FormLabel marginBottom="0" flex="1">Habilitar Fecha de Expiración:</FormLabel>
          <Switch {...controls.has_expiration_date} colorScheme="cyan" />
        </FormControl>

        {hasExpirationDate &&
          <FormControl isInvalid={!!errors.expiration_date} marginBottom="xl">
            <FormLabel>Fecha de expiración<Required /></FormLabel>
            <Controller
              name="expiration_date"
              control={control}
              render={({ field }) => (
                <DatePicker
                  selected={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  minDate={new Date()}
                  locale="es"
                  dateFormat="dd/MM/yyyy"
                  customInput={<Input />}
                />
              )}
            />
            <FormErrorMessage>{errors.expiration_date?.message}</FormErrorMessage>
          </FormControl>
        }

        <Flex justifyContent="flex-end" marginTop="xxl">
          <Button colorScheme="button-primary" type="submit" width="100%" isDisabled={creatingLink}>
            {creatingLink ? <Spinner size="sm" /> : 'Generar Link'}
          </Button>
        </Flex>
      </form>
    </Box>
  );
};
